// audio_utils.js

/** Plays the start audio, sets its volume to 1, and resets the audio element. */
export const StartAudio = () => {
  const startAudio = new Audio("/audio/start-13691.mp3");
  startAudio.volume = 1;
  startAudio.play().catch((error) => {
    console.error("Error playing start audio:", error);
  });
};

/** Plays the end audio, sets its volume to 1, and resets the audio element. */
export const EndAudio = () => {
  const endAudio = new Audio("/audio/stop-13692.mp3");
  endAudio.volume = 1;
  endAudio.play().catch((error) => {
    console.error("Error playing end audio:", error);
  });
};

/** Plays the instruction audio only once and sets up an event listener for when it ends.
 * @param {Audio} audio - The audio object for instructions.
 * @param {Function} handleAudioEnded - The function to call when the audio ends.
 * @param {boolean} audioPlayed - Flag to indicate if the audio has been played.
 * @param {Function} setAudioPlayed - State setter function to update the audioPlayed flag.
 */
export const playInstructionAudio = (audio, handleAudioEnded, audioPlayed, setAudioPlayed) => {
  if (!audioPlayed) {
    audio.play()
      .then(() => {
        audio.addEventListener("ended", handleAudioEnded);
        setAudioPlayed(true);
      })
      .catch(() => {
        if (window.confirm("Click 'OK' to hear instructions.")) {
          // Retry playing the audio if user confirms
          audio.play()
          audio.addEventListener("ended", handleAudioEnded);
          setAudioPlayed(true);
        }
      });
  }
};

/** Handles the event when the audio ends, enabling the button and updating the UI state.
 * @param {Function} setButtonDisabled - State setter to enable or disable the button.
 * @param {Function} setRecordingButtonDisabled - State setter to enable or disable recording button. 
 * @param {Function} setImageButtonContainerStyle - State setter to update the button container style.
 * @param {Function} setNextButtonStyle - State setter to update the next button style.
 * @param {Audio} audio - The audio object to stop and remove the event listener from.
 * @param {Function} setAudioPlayed - State setter to mark the audio as played.
 */
export const handleAudioEnded = (setButtonDisabled, setRecordingButtonDisabled, setImageButtonContainerStyle, setNextButtonStyle, audio, setAudioPlayed, ImageButtonContainerStyle='ImageButtonContainerEnabled') => {
  setButtonDisabled(false);
  setRecordingButtonDisabled(false);
  setImageButtonContainerStyle(ImageButtonContainerStyle);
  setNextButtonStyle("NextButtonEnabled");
  audio.removeEventListener("ended", handleAudioEnded);
  audio.pause();
  setAudioPlayed(true);
};

/**
 * Handles playing an audio instruction and triggers a callback when audio ends.
 * @param {HTMLAudioElement} audio - The audio element to play.
 * @param {Function} onEnded - Callback function to execute when audio ends.
 */
export function handleInstructions(audio, onEnded) {
  const nav = performance.getEntriesByType('navigation')[0];
  const isReload = nav?.type === 'reload';
  const go = () => { audio.play().catch(()=>{}); audio.addEventListener('ended', onEnded); };
  isReload
    ? window.addEventListener('pointerdown', go, { once:true })
    : go();
}

/**
 * Plays instruction audio once per render cycle and attaches the provided onEnded handler.
 * Guards on the caller's `audioPlayed` flag to avoid replays.
 */
export function playInstructionsOnce({ audio, audioPlayed, setAudioPlayed, onEnded }) {
  if (audioPlayed || !audio) return;
  const nav = performance.getEntriesByType('navigation')[0];
  const isReload = nav?.type === 'reload';

  const playWithFallback = () => {
    if (onEnded) audio.addEventListener("ended", onEnded);
    const playPromise = audio.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        // If autoplay is blocked, try again after the next user gesture.
        const retry = () => {
          audio.play().catch(() => {});
        };
        window.addEventListener("pointerdown", retry, { once: true });
      });
    }
  };

  // On hard reload, immediately enable progression once so UI isn't blocked.
  const skipOnce = isReload && !window.__instructionsReloadHandled;
  if (skipOnce) {
    window.__instructionsReloadHandled = true;
    onEnded?.();
    // window.addEventListener("pointerdown", playWithFallback, { once: true });    // Line to enable instruction to play after additional click
    setAudioPlayed(true);
    return;
  }

  playWithFallback();
  setAudioPlayed(true);
}

/**
 * Shared handler for instruction audio completion. Turns on controls and stops the audio.
 * Optional setters let callers adapt to different UI variants.
 */
export function handleAudioEndedCommon({
  audio,
  onEnded,
  setAudioPlayed,
  setButtonDisabled,
  setRecordingButtonDisabled,
  setImageButtonContainerStyle,
  imageButtonEnabledClass = "ImageButtonContainerEnabled",
  setNextButtonStyle,
}) {
  if (setButtonDisabled) setButtonDisabled(false);
  if (setRecordingButtonDisabled) setRecordingButtonDisabled(false);
  if (setImageButtonContainerStyle) setImageButtonContainerStyle(imageButtonEnabledClass);
  if (setNextButtonStyle) setNextButtonStyle("NextButtonEnabled");
  if (audio && onEnded) audio.removeEventListener("ended", onEnded);
  if (audio) audio.pause();
  if (setAudioPlayed) setAudioPlayed(true);
}
